/**
 * VideoMoments — renders a ```vidya-video-moments``` fenced block as a list of
 * clickable moment cards. The locate / navigate path (find_video_moments) emits
 * the block as JSON `[{ sec, label }]`; each card seeks the player on click.
 *
 * Robust to partial/malformed JSON (mid-stream): renders nothing rather than a
 * raw blob, so a half-streamed block never flashes ugly text at the student.
 */
import React from 'react';

interface Moment {
  sec: number;
  end?: number;
  label: string;
}

function mmss(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export const VideoMoments: React.FC<{ body: string; onSeek?: (sec: number) => void }> = ({
  body,
  onSeek,
}) => {
  let moments: Moment[] = [];
  try {
    const parsed = JSON.parse((body ?? '').trim());
    if (Array.isArray(parsed)) {
      moments = parsed.filter(
        (m) => m && typeof m.sec === 'number' && typeof m.label === 'string',
      );
    }
  } catch {
    // partial (still streaming) or malformed — show nothing, not a raw blob.
    return null;
  }
  if (!moments.length) return null;

  return (
    <div className="not-prose my-3 flex flex-col gap-2 font-sans">
      {moments.map((m, i) => {
        const range = `${mmss(m.sec)}${typeof m.end === 'number' && m.end > m.sec ? `–${mmss(m.end)}` : ''}`;
        return (
          <button
            key={`${m.sec}-${i}`}
            type="button"
            onClick={() => onSeek?.(m.sec)}
            className="group flex w-full items-center gap-3 rounded-xl border border-slate-200/70 bg-white px-2.5 py-2.5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-cyan-400 hover:shadow-md dark:border-slate-700/50 dark:bg-slate-800/40 dark:hover:border-cyan-500/60 dark:hover:bg-slate-800/70"
          >
            {/* Play affordance — fills cyan on hover */}
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cyan-500/10 text-cyan-600 transition-all group-hover:bg-cyan-500 group-hover:text-white group-hover:shadow-glow-cyan dark:bg-cyan-500/15 dark:text-cyan-300 dark:group-hover:bg-cyan-500 dark:group-hover:text-white">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="ml-0.5 h-4 w-4"><path d="M8 5v14l11-7z" /></svg>
            </span>
            {/* Label (headline) + time range */}
            <span className="min-w-0 flex-1">
              <span className="block whitespace-normal break-words text-[13px] font-medium leading-snug text-slate-700 group-hover:text-slate-900 dark:text-slate-100 dark:group-hover:text-white">
                {m.label}
              </span>
              <span className="mt-0.5 block text-[11px] font-semibold tabular-nums text-cyan-600 dark:text-cyan-400">
                {range}
              </span>
            </span>
            {/* Jump chevron */}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden className="h-4 w-4 shrink-0 text-slate-300 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-cyan-500 dark:text-slate-600 dark:group-hover:text-cyan-400">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        );
      })}
    </div>
  );
};

export default VideoMoments;
